import { track } from '../../track';
import {
	getSectionFromHref,
	getTargetTypeFromHref
} from '../getSectionFromHref/getSectionFromHref';
import {
	UNKNOWN_SECTION,
	type OnwardClickProperties,
	type Section,
	type SectionConfig,
	type TargetType
} from '../types';

export type TrackOnwardClickInput = {
	/**
	 * The event to emit, e.g. `NEWS/ONWARD_CLICK`.
	 *
	 * Passed in rather than fixed here because sites name their events per
	 * section, and the name a crossing is reported under depends on where it
	 * started — which only the call site knows.
	 */
	eventName: string;
	/** The site's route map. Sites bind this once in their own wrapper. */
	config: SectionConfig;
	/** Where the click happened. Defaults to the current page's section. */
	fromSection?: Section;
	/** The link's destination. `toSection`/`targetType` are derived from it. */
	href: string | null | undefined;
	/** The element clicked — `nav`, `drop-card`, `artist-link`, … */
	sourceElement: string;
	/** Overrides the depth-derived target type when the call site knows better. */
	targetType?: TargetType;
	/** The artist the click carries through, when there is one. */
	artist?: string | null;
};

/**
 * Fires `onward_click` for an internal navigation that crosses sections (HB-967).
 *
 * Same-section clicks are deliberately dropped. The event exists to measure the
 * News→commerce bridge, and paging around inside News would otherwise bury the
 * crossings under an order of magnitude more in-section noise. `link_out` cannot
 * cover this ground either — these are internal navigations, so the SDK's
 * outgoing-link tracking never sees them.
 *
 * Returns whether the event was sent, which keeps the same-section skip testable
 * without reaching into the OpenPanel mock.
 */
export const trackOnwardClick = ({
	eventName,
	config,
	fromSection,
	href,
	sourceElement,
	targetType,
	artist
}: TrackOnwardClickInput): boolean => {
	const from =
		fromSection ??
		(typeof window === 'undefined'
			? UNKNOWN_SECTION
			: getSectionFromHref(window.location.pathname, config));
	const to = getSectionFromHref(href, config);

	// An unresolvable destination (empty, malformed or off-site href) is not an
	// onward journey.
	if (!href || to === UNKNOWN_SECTION) return false;

	if (from === to) return false;

	const properties: OnwardClickProperties = {
		from_section: from,
		to_section: to,
		target_type: targetType ?? getTargetTypeFromHref(href, config),
		source_element: sourceElement,
		...(artist ? { artist } : {})
	};

	track(eventName, properties);

	return true;
};
